/**
 * Zod helpers — the SINGLE documented cast site for branded types.
 *
 * Every branded schema is `z.number().int().min().max().transform(v => v as B)`
 * or `z.string().min().max().transform(v => v as B)`, and the cast happens
 * INSIDE this file only. Nothing else in the codebase is allowed to write
 * `x as Tick` etc. — go through the constructors in `constructors.ts`.
 *
 * Also: this file is where `-0` → `0` normalization happens at parse time.
 * `Object.is(-0, 0)` is false, and hashing paths in WP3 diverge on it.
 * (See wp1-protocol.md "Traps".)
 */

import { z } from "zod";

/**
 * Factory for a branded integer schema. Produces a zod schema whose parsed
 * output has the brand type `B`. This is the ONE cast site — audit `as` in
 * this file only.
 *
 * The transform:
 *   1. Rejects non-integers (`z.int()`).
 *   2. Rejects NaN/Infinity (zod's `number()` rejects NaN; `.int()` rejects
 *      non-finite).
 *   3. Enforces min/max bounds.
 *   4. Normalizes `-0` → `+0`.
 */
export function brandedInt<B extends number>(
  brand: string,
  min: number,
  max: number,
): z.ZodType<B> {
  return z
    .number()
    .int(`${brand}: expected integer`)
    .gte(min, `${brand}: below minimum ${String(min)}`)
    .lte(max, `${brand}: above maximum ${String(max)}`)
    .transform((v) => (v === 0 ? 0 : v) as B);
}

/**
 * Factory for a branded string schema. Enforces non-empty + max length; the
 * cast to `B` is the documented exception.
 *
 * Optional `pattern` regex is applied *after* length checks (zod evaluates
 * left-to-right; put the cheap check first for better errors).
 */
export function brandedString<B extends string>(
  brand: string,
  minLen: number,
  maxLen: number,
  pattern?: RegExp,
): z.ZodType<B> {
  let schema = z
    .string()
    .min(minLen, `${brand}: length below ${String(minLen)}`)
    .max(maxLen, `${brand}: length above ${String(maxLen)}`);
  if (pattern !== undefined) {
    schema = schema.regex(pattern, `${brand}: pattern mismatch`);
  }
  return schema.transform((v) => v as B);
}
